import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TablePagination,
} from "@mui/material";
// @mui icons
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
// components
import Page from "../../../components/Page";
import Scrollbar from "../../../components/Scrollbar";
// utils
import { fDate, fDateTime } from "../../../utils/formatTime";
//
import orderService from "../services/orderService";
import AlertMessage from "../../../components/alert/alert";

export default function OperationalReport() {
  const [matrix, setMatrix] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [branch, setBranch] = useState("Todas");
  const [customerInitials, setCustomerInitials] = useState("");

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Details Modal
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [matrixDetails, setMatrixDetails] = useState<any[]>([]);
  const [selectionInfo, setSelectionInfo] = useState({ lens: "", stage: "" });
  const [detailsPage, setDetailsPage] = useState(0);
  const [detailsRowsPerPage, setDetailsRowsPerPage] = useState(10);

  const fetchMatrix = async () => {
    try {
      setLoading(true);
      const start = startDate
        ? new Date(`${startDate}T00:00:00`).getTime() / 1000
        : null;
      const end = endDate
        ? new Date(`${endDate}T23:59:59`).getTime() / 1000
        : null;

      const response = await orderService.getOperationalMatrix(
        start,
        end,
        branch,
        customerInitials,
      );
      setMatrix(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      setErrorMessage("Erro ao carregar relatório operacional");
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = async (
    lens: string,
    stage: string,
    value: number,
  ) => {
    if (value === 0 || loading) return;

    try {
      setSelectionInfo({ lens, stage });
      setOpenDetails(true);
      setDetailsLoading(true);
      setDetailsPage(0); // Reset page when opening new details

      const start = startDate
        ? new Date(`${startDate}T00:00:00`).getTime() / 1000
        : null;
      const end = endDate
        ? new Date(`${endDate}T23:59:59`).getTime() / 1000
        : null;

      const response = await orderService.getOperationalMatrixDetails(
        lens,
        stage,
        start,
        end,
        branch,
        customerInitials,
      );
      setMatrixDetails(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      setErrorMessage("Erro ao carregar detalhes do relatório");
      setShowError(true);
      setOpenDetails(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchMatrix();
  }, [startDate, endDate, branch, customerInitials]);

  return (
    <Page title="Relatório Operacional">
      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Relatório Operacional
          </Typography>
        </Stack>

        <Card>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ p: 3 }}
            alignItems="center"
          >
            <TextField
              label="Data Inicial"
              type="date"
              value={startDate}
              onChange={(e: any) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Data Final"
              type="date"
              value={endDate}
              onChange={(e: any) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />

            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Unidade</InputLabel>
              <Select
                value={branch}
                label="Unidade"
                onChange={(e) => setBranch(e.target.value)}
              >
                <MenuItem value="Todas">Todas</MenuItem>
                <MenuItem value="SP">SP</MenuItem>
                <MenuItem value="PA">PA</MenuItem>
                <MenuItem value="NE">NE</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Sigla Cliente"
              value={customerInitials}
              onChange={(e: any) => setCustomerInitials(e.target.value)}
              size="small"
              placeholder="XXX"
              inputProps={{ maxLength: 3 }}
            />

            <Button
              variant="contained"
              onClick={fetchMatrix}
              disabled={loading}
              sx={{
                backgroundColor: "#004A78",
                "&:hover": {
                  backgroundColor: "#226C9A",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Filtrar"
              )}
            </Button>
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>PRODUTO</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      RECEBIDA
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      PERDA
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      RETRABALHO
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      REPROVADA NO RECEBIMENTO
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      CANCELADA
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      CADASTRADA
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      BAIXA
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : matrix.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography variant="subtitle1" color="textSecondary">
                          Nenhum dado encontrado para os filtros selecionados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    matrix.map((row: any, index: number) => {
                      const stages = [
                        "RECEBIDA",
                        "PERDA",
                        "RETRABALHO",
                        "REPROVADA NO RECEBIMENTO",
                        "CANCELADA",
                        "CADASTRADA",
                        "BAIXA",
                      ];

                      return (
                        <TableRow hover key={index}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ fontWeight: 700 }}
                          >
                            {row.produto}
                          </TableCell>
                          {stages.map((stage) => {
                            const key = stage.toLowerCase().replace(/ /g, "_");
                            const val = row[key];
                            const hasData = val > 0;
                            return (
                              <TableCell
                                key={stage}
                                align="center"
                                onClick={() =>
                                  hasData &&
                                  handleOpenDetails(row.produto, stage, val)
                                }
                                sx={{
                                  cursor: hasData ? "pointer" : "default",
                                  fontWeight: 700,
                                  color: hasData ? "#004A78" : "text.secondary",
                                  "&:hover": {
                                    bgcolor: hasData
                                      ? "action.hover"
                                      : "transparent",
                                  },
                                }}
                              >
                                {hasData ? Number(val).toFixed(1) : "-"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>

        {/* Details Dialog */}
        <Dialog
          open={openDetails}
          onClose={() => setOpenDetails(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">
                Detalhes: {selectionInfo.lens} ({selectionInfo.stage})
              </Typography>
              <IconButton onClick={() => setOpenDetails(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            {detailsLoading ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#F4F6F8" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: "#004A78" }}>
                          CÓDIGO
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#004A78" }}>
                          CLIENTE
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#004A78" }}>
                          DATA
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 700, color: "#004A78" }}
                        >
                          PARES
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 700, color: "#004A78" }}
                        >
                          AÇÕES
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matrixDetails
                        .slice(
                          detailsPage * detailsRowsPerPage,
                          detailsPage * detailsRowsPerPage + detailsRowsPerPage,
                        )
                        .map((order: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {order.codigo}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {order.cliente}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {fDateTime(order.data * 1000)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {Number(order.pares).toFixed(1)}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  window.open(
                                    `/dashboard/buscar?code=${order.codigo}`,
                                    "_blank",
                                  )
                                }
                                sx={{ color: "#004A78" }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      {matrixDetails.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{ py: 3, fontWeight: 700 }}
                          >
                            Nenhum detalhe encontrado.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={matrixDetails.length}
                  rowsPerPage={detailsRowsPerPage}
                  page={detailsPage}
                  onPageChange={(_, newPage) => setDetailsPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setDetailsRowsPerPage(parseInt(event.target.value, 10));
                    setDetailsPage(0);
                  }}
                  labelRowsPerPage="Linhas por página"
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetails(false)} color="inherit">
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <AlertMessage open={showError} message={errorMessage} />
      </Container>
    </Page>
  );
}
